import { createRouter, createWebHistory } from "vue-router";
import { useAuthStore } from "@/stores/auth.js";
import { refreshAxios } from "@/composables/useApi";
const GuestLayout = () => import("@/layouts/Guest.vue");
const ProfileLayout = () => import("@/layouts/Profile.vue");
const Register = () => import("@/pages/auth/Register.vue");
const Login = () => import("@/pages/auth/Login.vue");
const ForgotPassword = () => import("@/pages/auth/ForgotPassword.vue");
const ResetPassword = () => import("@/pages/auth/ResetPassword.vue");
const VerifyEmail = () => import("@/pages/auth/VerifyEmail.vue");
const ContactUs = () => import("@/pages/ContactUs.vue");
const AboutUs = () => import("@/pages/AboutUs.vue");

const Home = () => import("@/pages/Home.vue");
const Search = () => import("@/pages/Search.vue");
const News = () => import("@/pages/News.vue");
const NewsDetails = () => import("@/pages/NewsDetails.vue");
const RelatedLinks = () => import("@/pages/RelatedLinks.vue");
const Blog = () => import("@/pages/Blog.vue");
const UserGuide = () => import("@/pages/UserGuide.vue");
const NotFound = () => import("@/pages/NotFound.vue");
const ComingSoon = () => import("@/pages/ComingSoon.vue");
const Advertisement = () => import("@/pages/Advertisement.vue");
const AdvertisementDetails = () => import("@/pages/AdvertisementDetails.vue");
const BlogDetails = () => import("@/pages/BlogDetails.vue");
const Multimedia = () => import("@/pages/Multimedia.vue");
const Broadcast = () => import("@/pages/Broadcast.vue");
const DownloadableFiles = () => import("@/pages/DownloadableFiles.vue");
const Faq = () => import("@/pages/Faq.vue");
const LegalTexts = () => import("@/pages/LegalTexts.vue");
const FriendsLinks = () => import("@/pages/FriendsLinks.vue");
const Privacy = () => import("@/pages/Privacy.vue");
const Terms = () => import("@/pages/Terms.vue");
const SiteMap = () => import("@/pages/Sitemap.vue");
const PersonalPrivacy = () => import("@/pages/PersonalPrivacy.vue");
const BookAppointment = () => import("@/pages/BookAppointment.vue");
const JoinUs = () => import("@/pages/JoinUs.vue");
const ShareSurvey = () => import("@/pages/ShareSurvey.vue");
const Services = () => import("@/pages/services/Main.vue");
const ServiceDetails = () => import("@/pages/services/Details.vue");
const Announcements = () => import("@/pages/announcements/Main.vue");
const Sales = () => import("@/pages/announcements/Sales.vue");
const BranshDetails = () => import("@/pages/announcements/BranshDetails.vue");
const AnnoucementDetails = () => import("@/pages/announcements/Details.vue");
const CreateAnnoucement = () =>
    import("@/pages/announcements/CreateAnnoucement.vue");
const EditAnnoucement = () =>
    import("@/pages/announcements/EditAnnoucement.vue");

const Profile = () => import("@/pages/profile/Main.vue");
const ProfileAnnouncements = () => import("@/pages/profile/Announcements.vue");
const FavoritesAnnouncements = () => import("@/pages/profile/Favorites.vue");
const myAdvertisements = () => import("@/pages/profile/Advertisements.vue");
const myProducts = () => import("@/pages/profile/Products.vue");
const myAppointments = () => import("@/pages/profile/Appointments.vue");
const MyJoinRequest = () => import("@/pages/profile/MyJoinRequest.vue");

const routes = [
    {
        path: "/",
        component: GuestLayout,
        redirect: { name: "Home" },
        meta: { requiresAuth: false },
        children: [
            { path: "/home", name: "Home", component: Home },
            { path: "/about-us", name: "AboutUs", component: AboutUs },
            { path: "/sitemap", name: "SiteMap", component: SiteMap },
            { path: "/search", name: "Search", component: Search },
            { path: "/contact-us", name: "ContactUs", component: ContactUs },
            { path: "/news", name: "News", component: News },
            { path: "/news/:id", name: "NewsDetails", component: NewsDetails },
            { path: "/blog", name: "Blog", component: Blog },
            {
                path: "/blog/:slug",
                name: "BlogDetails",
                component: BlogDetails,
            },
            {
                path: "/related-links",
                name: "RelatedLinks",
                component: RelatedLinks,
            },
            {
                path: "/advertisement",
                name: "Advertisement",
                component: Advertisement,
                meta: { requiresAuth: true },
            },
            {
                path: "/advertisement/:id",
                name: "AdvertisementDetails",
                component: AdvertisementDetails,
                meta: { requiresAuth: true },
            },
            { path: "/multimedia", name: "Multimedia", component: Multimedia },
            { path: "/podcast", name: "Broadcast", component: Broadcast },
            {
                path: "/downloadable-files",
                name: "DownloadableFiles",
                component: DownloadableFiles,
            },
            { path: "/faq", name: "Faq", component: Faq },
            { path: "/legal-texts", name: "LegalTexts", component: LegalTexts },
            {
                path: "/friendly-links",
                name: "FriendsLinks",
                component: FriendsLinks,
            },
            { path: "/privacy-policy", name: "Privacy", component: Privacy },
            {
                path: "/terms-and-conditions",
                name: "TermsConditions",
                component: Terms,
            },
            {
                path: "/personal-privacy",
                name: "PersonalPrivacy",
                component: PersonalPrivacy,
            },
            {
                path: "/join-us",
                name: "JoinUs",
                component: JoinUs,
                meta: { requiresAuth: true },
            },
            { path: "/user-guide", name: "UserGuide", component: UserGuide },
            { path: "/coming-soon", name: "ComingSoon", component: ComingSoon },
            {
                path: "/book-appointment",
                name: "BookAppointment",
                component: BookAppointment,
                meta: { requiresAuth: true },
            },
            {
                path: "/share-survey-with-us",
                name: "ShareSurvey",
                component: ShareSurvey,
            },
        ],
    },
    {
        path: "/services",
        redirect: "/services/all",
        component: GuestLayout,
        meta: { isGuest: true },
        children: [
            { path: "/services/all", name: "Services", component: Services },
            {
                path: "/services/:id",
                name: "ServiceDetails",
                component: ServiceDetails,
            },
        ],
    },
    {
        path: "/profile",
        redirect: "/profile/main",
        component: ProfileLayout,
        meta: { isGuest: false, requiresAuth: true },
        children: [
            { path: "main", name: "Profile", component: Profile },
            {
                path: "announcements",
                name: "ProfileAnnouncements",
                component: ProfileAnnouncements,
            },
            {
                path: "favorites",
                name: "FavoritesAnnouncements",
                component: FavoritesAnnouncements,
            },
            { path: "products", name: "myProducts", component: myProducts },
            {
                path: "appointments",
                name: "myAppointments",
                component: myAppointments,
            },
            {
                path: "advertisements",
                name: "myAdvertisements",
                component: myAdvertisements,
            },
            {
                path: "join-requests",
                name: "MyJoinRequest",
                component: MyJoinRequest,
            },
        ],
    },
    {
        path: "/announcements",
        redirect: "/announcements",
        component: GuestLayout,
        children: [
            {
                path: "/announcements",
                name: "Announcements",
                component: Announcements,
            },
            {
                path: "/announcements/show/:slug",
                name: "AnnoucementDetails",
                component: AnnoucementDetails,
            },
            {
                path: "/announcements/:id",
                name: "BranshDetails",
                component: BranshDetails,
            },
            {
                path: "/announcements/:id/create",
                name: "CreateAnnoucement",
                component: CreateAnnoucement,
                meta: { requiresAuth: true, isGuest: false },
            },
            {
                path: "/announcements/:id/edit/:slug",
                name: "EditAnnoucement",
                component: EditAnnoucement,
                meta: { requiresAuth: true, isGuest: false },
            },
        ],
    },
    {
        path: "/sales",
        redirect: "/sales",
        component: GuestLayout,
        children: [
            { path: "/sales", name: "Sales", component: Sales },
            {
                path: "/sales/show/:slug",
                name: "SalesDetails",
                component: AnnoucementDetails,
            },
            {
                path: "/sales/:id",
                name: "SalesBranshDetails",
                component: BranshDetails,
            },
            {
                path: "/sales/:id/create",
                name: "CreateSalesAnnoucement",
                component: CreateAnnoucement,
                meta: { requiresAuth: true, isGuest: false },
            },
            {
                path: "/sales/:id/edit/:slug",
                name: "EditSalesAnnoucement",
                component: EditAnnoucement,
                meta: { requiresAuth: true, isGuest: false },
            },
        ],
    },
    {
        path: "/auth",
        redirect: "/login",
        component: GuestLayout,
        meta: { isGuest: true },
        children: [
            { path: "/register", name: "Register", component: Register },
            { path: "/login", name: "Login", component: Login },
            {
                path: "/verify-email",
                name: "VerifyEmail",
                component: VerifyEmail,
            },
            {
                path: "/forgot-password",
                name: "ForgotPassword",
                component: ForgotPassword,
            },
            {
                path: "/reset-password/:token",
                name: "ResetPassword",
                component: ResetPassword,
            },
        ],
    },
    {
        path: "/:pathMatch(.*)*",
        name: "404",
        component: NotFound,
    },
];

const router = createRouter({
    history: createWebHistory(),
    routes: routes,
});

router.beforeEach((to, from, next) => {
    refreshAxios();
    const authStore = useAuthStore();

    if (from.query == to.query || Object.keys(to.query).length == 0) {
        scrollToTop();
    }

    if (to.meta.requiresAuth && !authStore.currentUser) {
        next({
            name: "Login",
            query: {
                redirect: to.name,
            },
        });
    } else if (to.meta.isGuest && authStore.currentUser) {
        if (from.query.redirect) {
            next({ name: from.query.redirect });
        }
        next();
    } else {
        next();
    }
});

export default router;
